import Vue from 'vue';
import VueRouter, { NavigationGuard, NavigationGuardNext, Route } from 'vue-router';
import oauth from '@/plugins/oauth';
import i18n from '@/plugins/vue-i18n';

import { addGtmEvent, GTMEvent } from '@/helpers/gtm';
import { buildSiteUrl } from '@/helpers/external';
import { Apps } from '@/constants/apps';
import MainStorage from '@/storage/main';
import { LocaleCode } from '@/locales';

function getAppName(path: string): Apps | undefined {
  let result;
  Object.values(Apps).forEach(appName => {
    if (path.includes(appName)) {
      result = appName;
    }
  });
  return result;
}

const checkAuthGuard: NavigationGuard = function(to, from, next) {
  const appName = getAppName(to.path);
  Vue.prototype.$appName = appName;

  let authorized = false;
  if (appName !== undefined) {
    authorized = oauth.checkAuthorized(appName);
  }

  if (to.matched.some(record => record.meta.requiresAnon) && authorized) {
    return next({ name: 'main' });
  }

  if (to.matched.some(record => record.meta.requiresAuth) && !authorized) {
    return next({ name: 'main' });
  }

  next();
};

const passFromRouteGuard: NavigationGuard = function(to, from, next) {
  Object.assign(to.params, { from: from.name });

  next();
};

const trackLandingGuard = function(to: Route) {
  if (!to.matched.some(record => record.meta.impression)) {
    return;
  }

  addGtmEvent(GTMEvent.LANDING_IMPRESSION);
};

const legalRedirectGuard: NavigationGuard = function({ meta = {} }, from, next) {
  const { redirect } = meta;

  if (!redirect) {
    return next({ name: 'main' });
  }

  const link = buildSiteUrl(redirect, i18n.locale);

  window.location.replace(link);
};

type NavigationGuardFactory = (router: VueRouter) => NavigationGuard;

const townshipLocaleGuardFactory: NavigationGuardFactory = router => {
  return (to: Route, from: Route, next: NavigationGuardNext) => {
    if (to.path.includes('township') && MainStorage.getLocale() === LocaleCode.RU) {
      MainStorage.saveLocale(LocaleCode.EN);
      router.app.$i18n.locale = LocaleCode.EN; // eslint-disable-line no-param-reassign
    }

    next();
  };
};

export {
  checkAuthGuard,
  passFromRouteGuard,
  trackLandingGuard,
  townshipLocaleGuardFactory,
  legalRedirectGuard,
};
